import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Mail, Instagram, Facebook, Send, CheckCircle2, MessageCircle } from "lucide-react";
import { ACADEMY } from "../lib/academy-data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGE_OPTIONS = [
  "כיתה ט'",
  "כיתה י'",
  "כיתה י\"א",
  "כיתה י\"ב",
  "אחר",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    age: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("יש למלא שם ומספר טלפון");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.age) delete payload.age;
      if (!payload.message) delete payload.message;
      await axios.post(`${API}/contact`, payload);
      toast.success("הפנייה התקבלה — נחזור אליכם בקרוב!");
      setSubmitted(true);
      setForm({ name: "", phone: "", email: "", age: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("אירעה שגיאה בשליחת הפנייה. נסו שוב או חייגו אלינו.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-24 md:py-28 overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 diagonal-stripe opacity-40" />
        <div className="container mx-auto px-6 md:px-10 relative">
          <p className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] text-[#DC2626] uppercase mb-4">
            צור קשר
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-[0.9] max-w-4xl">
            יש שאלה?
            <br />
            <span className="text-[#DC2626]">בואו נדבר.</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-neutral-300 mt-6 leading-relaxed max-w-3xl">
            השאירו לנו פרטים ונחזור אליכם, או צרו קשר ישירות עם {ACADEMY.manager}, מנהל האקדמיה.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-20 md:py-24 bg-[#050505]">
        <div className="container mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7 bg-[#171717] border border-white/10 p-8 md:p-10">
            <h2 className="font-heading text-2xl md:text-3xl font-black text-white uppercase mb-6">
              השאירו פרטים
            </h2>

            {submitted ? (
              <div className="flex flex-col items-center text-center py-12" data-testid="contact-success">
                <CheckCircle2 size={64} strokeWidth={2} className="text-[#DC2626] mb-4" />
                <h3 className="font-heading text-2xl font-bold text-white mb-2">תודה!</h3>
                <p className="font-body text-neutral-400 mb-6">
                  הפנייה התקבלה בהצלחה. נחזור אליכם בהקדם האפשרי.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="font-heading text-sm font-bold uppercase tracking-wider text-[#DC2626] hover:text-white transition-colors"
                  data-testid="contact-send-another"
                >
                  שליחת פנייה נוספת
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-heading text-xs font-bold tracking-wider text-neutral-400 uppercase mb-2">
                      שם מלא *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={updateField("name")}
                      required
                      placeholder="הכניסו את שמכם"
                      className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#DC2626] focus:outline-none text-white px-4 py-3 font-body transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block font-heading text-xs font-bold tracking-wider text-neutral-400 uppercase mb-2">
                      טלפון *
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={updateField("phone")}
                      required
                      placeholder="050-0000000"
                      dir="ltr"
                      className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#DC2626] focus:outline-none text-white px-4 py-3 font-body transition-colors text-right"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-heading text-xs font-bold tracking-wider text-neutral-400 uppercase mb-2">
                      אימייל
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={updateField("email")}
                      placeholder="email@example.com"
                      dir="ltr"
                      className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#DC2626] focus:outline-none text-white px-4 py-3 font-body transition-colors text-right"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block font-heading text-xs font-bold tracking-wider text-neutral-400 uppercase mb-2">
                      שכבת גיל
                    </label>
                    <select
                      value={form.age}
                      onChange={updateField("age")}
                      className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#DC2626] focus:outline-none text-white px-4 py-3 font-body transition-colors"
                      data-testid="contact-age-select"
                    >
                      <option value="">בחרו שכבה</option>
                      {AGE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block font-heading text-xs font-bold tracking-wider text-neutral-400 uppercase mb-2">
                    הודעה
                  </label>
                  <textarea
                    value={form.message}
                    onChange={updateField("message")}
                    rows={5}
                    placeholder="ספרו לנו על השחקן/ית, ניסיון קודם, ושאלות..."
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#DC2626] focus:outline-none text-white px-4 py-3 font-body transition-colors resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-neutral-700 disabled:cursor-not-allowed text-white font-heading font-bold text-base uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-300"
                  data-testid="contact-submit-btn"
                >
                  {loading ? "שולח..." : "שלחו פנייה"}
                  {!loading && <Send size={18} strokeWidth={3} />}
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <aside className="lg:col-span-5 flex flex-col gap-5">
            <div className="bg-[#171717] border border-white/10 p-7">
              <h3 className="font-heading text-xl font-bold text-white mb-5 uppercase">
                פרטי קשר
              </h3>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <div className="w-11 h-11 inline-flex items-center justify-center bg-[#DC2626] text-white flex-shrink-0">
                    <MapPin size={20} strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
                      מיקום
                    </p>
                    <p className="font-body text-base text-white">{ACADEMY.location}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-11 h-11 inline-flex items-center justify-center bg-[#DC2626] text-white flex-shrink-0">
                    <Phone size={20} strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
                      טלפון
                    </p>
                    <a
                      href={`tel:${ACADEMY.managerPhone.replace(/-/g, "")}`}
                      className="font-body text-base text-white hover:text-[#DC2626] transition-colors"
                      dir="ltr"
                      data-testid="contact-info-phone"
                    >
                      {ACADEMY.managerPhone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-11 h-11 inline-flex items-center justify-center bg-[#DC2626] text-white flex-shrink-0">
                    <Mail size={20} strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
                      איש קשר
                    </p>
                    <p className="font-body text-base text-white">{ACADEMY.manager}</p>
                    <p className="font-body text-sm text-neutral-400">{ACADEMY.managerRole}</p>
                  </div>
                </li>
              </ul>

              {/* Direct contact buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 pt-6 border-t border-white/10">
                <a
                  href={`https://wa.me/${ACADEMY.managerWhatsapp}?text=${encodeURIComponent(
                    "היי מעיין, אני מתעניין/ת באקדמיית הכדורסל הפועל מטה יהודה."
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB957] text-white font-heading font-bold text-sm uppercase tracking-wider px-4 py-3 rounded-sm transition-all"
                  data-testid="contact-whatsapp-btn"
                >
                  <MessageCircle size={18} strokeWidth={2.5} fill="currentColor" />
                  WhatsApp
                </a>
                <a
                  href={`mailto:${ACADEMY.managerEmail}?subject=${encodeURIComponent(
                    "פנייה — אקדמיית כדורסל"
                  )}`}
                  className="group inline-flex items-center justify-center gap-2 bg-white hover:bg-neutral-100 text-[#0A0A0A] font-heading font-bold text-sm uppercase tracking-wider px-4 py-3 rounded-sm transition-all"
                  data-testid="contact-email-btn"
                >
                  <Mail size={18} strokeWidth={2.5} />
                  אימייל
                </a>
              </div>
              <p className="font-body text-xs text-neutral-500 text-center mt-3" dir="ltr">
                {ACADEMY.managerEmail}
              </p>
            </div>

            <div className="bg-[#171717] border border-white/10 p-7">
              <h3 className="font-heading text-xl font-bold text-white mb-5 uppercase">
                עקבו אחרינו
              </h3>
              <p className="font-body text-sm text-neutral-400 mb-5">
                התעדכנו בכל מה שקורה במועדון ובאקדמיה
              </p>
              <div className="flex gap-3">
                <a
                  href={ACADEMY.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-white/15 hover:border-[#DC2626] hover:bg-[#DC2626] text-white font-heading font-bold text-sm uppercase tracking-wider px-5 py-3 transition-all"
                  data-testid="contact-instagram-link"
                >
                  <Instagram size={18} strokeWidth={2.5} />
                  Instagram
                </a>
                <a
                  href={ACADEMY.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-white/15 hover:border-[#DC2626] hover:bg-[#DC2626] text-white font-heading font-bold text-sm uppercase tracking-wider px-5 py-3 transition-all"
                  data-testid="contact-facebook-link"
                >
                  <Facebook size={18} strokeWidth={2.5} />
                  Facebook
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
